/**
 * CampaignManagement Component (Redesigned)
 *
 * Tiered expandable cards for managing campaign entities.
 * See docs/CAMPAIGN_MANAGEMENT_CARD_REDESIGN.md for full design spec.
 *
 * Key improvements over previous design:
 * - No dropdowns - all entities visible upfront as expandable cards
 * - Summary stats (keyword count, RSA count, etc.) visible before expanding
 * - One-click access to any entity's content
 * - Accordion behavior - only one entity expanded at a time
 * - Campaign-level assets in dedicated collapsible section
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import type { GetRDAResponse, GetDemandGenAdResponse } from '@/services/api/assets';
import type { CampaignManagementProps, EntityTab } from './types';
import { CampaignLevelCard } from './CampaignLevelCard';
import { EntityCardList } from './EntityCardList';
import {
  getEntities,
  getEntityType,
  getEntityId,
  hasEntities,
  shouldShowCampaignLevelAssets,
} from './utils';
import { isMetaCampaign } from '@/types/campaign';
import { MetaManagementAccordion } from './panels/meta/MetaManagementAccordion';

// Re-export types for external use
export type { EntityTab, EntityType, CampaignManagementProps } from './types';

export function CampaignManagement({
  campaign,
  hasFullStructure,
  selectedLandingPages,
  rdaCache,
  isLoadingRDAs,
  demandGenCache,
  isLoadingDemandGenAds,
  onCreateDemandGenAd,
}: CampaignManagementProps) {
  const { memberUuid } = useAuth();
  const queryClient = useQueryClient();

  // State for expanded entity (accordion behavior - only one at a time)
  const [expandedEntityId, setExpandedEntityId] = useState<string | null>(null);

  // State for remembering active tab per entity
  const [activeTabByEntity, setActiveTabByEntity] = useState<Record<string, EntityTab>>({});

  // State for campaign level section
  const [campaignLevelExpanded, setCampaignLevelExpanded] = useState(false);

  // State for editing RSA/RDA/Demand Gen ads
  const [editingRSA, setEditingRSA] = useState<{ adGroupId: string; adId: string } | null>(null);
  const [editingRDA, setEditingRDA] = useState<{ adGroupId: string; adId: string } | null>(null);
  const [editingDemandGenAd, setEditingDemandGenAd] = useState<{ adGroupId: string; adId: string } | null>(null);

  // Track if we've done the initial auto-expand (to avoid re-expanding on collapse)
  const hasAutoExpanded = useRef(false);

  // Get entities based on campaign type
  const entities = useMemo(() => getEntities(campaign), [campaign]);
  const entityType = useMemo(() => getEntityType(campaign.campaign_type || ''), [campaign.campaign_type]);
  const hasAnyEntities = hasEntities(campaign);
  const showCampaignLevel = shouldShowCampaignLevelAssets(campaign);

  // Auto-expand if only one entity (only on initial mount)
  useEffect(() => {
    if (entities.length === 1 && !hasAutoExpanded.current) {
      const id = getEntityId(entities[0], entityType);
      setExpandedEntityId(id);
      hasAutoExpanded.current = true;
    }
  }, [entities, entityType]);

  // Handle tab change for an entity
  const handleTabChange = (entityId: string, tab: EntityTab) => {
    setActiveTabByEntity(prev => ({ ...prev, [entityId]: tab }));
  };

  // Handle campaign level toggle
  const handleCampaignLevelToggle = () => {
    setCampaignLevelExpanded(prev => !prev);
  };

  // Handle entity expand (accordion behavior only among entity cards, not with campaign level)
  const handleExpandEntity = (entityId: string | null) => {
    setExpandedEntityId(entityId);
  };

  // Handle RSA edit request
  const handleEditRSA = (adGroupId: string, adId: string) => {
    setEditingRSA({ adGroupId, adId });
  };

  // Handle RDA edit request
  const handleEditRDA = (adGroupId: string, adId: string) => {
    setEditingRDA({ adGroupId, adId });
  };

  // Handle Demand Gen ad edit request
  const handleEditDemandGenAd = (adGroupId: string, adId: string) => {
    setEditingDemandGenAd({ adGroupId, adId });
  };

  // Convert RDA cache entry to GetRDAResponse format for editor
  const getRDAInitialData = useCallback((adId: string): GetRDAResponse | undefined => {
    const entry = rdaCache[adId];
    if (!entry) return undefined;
    return {
      success: true,
      headlines: entry.headlines,
      long_headline: entry.long_headline,
      descriptions: entry.descriptions || [],
      business_name: entry.business_name,
      final_urls: entry.final_urls || [],
      marketing_images: entry.marketing_images || [],
      square_marketing_images: entry.square_marketing_images || [],
      logo_images: entry.logo_images || [],
      landscape_logo_images: entry.landscape_logo_images || [],
    };
  }, [rdaCache]);

  // Convert Demand Gen cache entry to GetDemandGenAdResponse format for editor
  const getDemandGenAdInitialData = useCallback((adId: string): GetDemandGenAdResponse | undefined => {
    const entry = demandGenCache[adId];
    if (!entry) return undefined;
    return {
      success: true,
      ad_name: entry.ad_name,
      ad_type: entry.ad_type,
      headlines: entry.headlines || [],
      descriptions: entry.descriptions || [],
      business_name: entry.business_name,
      call_to_action: entry.call_to_action,
      final_urls: entry.final_urls || [],
      long_headlines: entry.long_headlines || [],
      video_ids: entry.video_ids || [],
      marketing_images: entry.marketing_images || [],
      square_marketing_images: entry.square_marketing_images || [],
      portrait_marketing_images: entry.portrait_marketing_images || [],
      logo_images: entry.logo_images || [],
      carousel_cards: entry.carousel_cards || [],
    };
  }, [demandGenCache]);

  // Handle editor saved - invalidate queries and clear editing state
  const handleEditorSaved = useCallback(() => {
    setEditingRSA(null);
    setEditingRDA(null);
    setEditingDemandGenAd(null);
    queryClient.invalidateQueries({ queryKey: ['campaign'] });
    queryClient.invalidateQueries({ queryKey: ['app-config'] });
    queryClient.invalidateQueries({ queryKey: ['rda-cache', campaign.campaign_id] });
    queryClient.invalidateQueries({ queryKey: ['demand-gen-cache', campaign.campaign_id] });
  }, [queryClient, campaign.campaign_id]);

  // Handle editor cancel - clear editing state
  const handleEditorCancel = useCallback(() => {
    setEditingRSA(null);
    setEditingRDA(null);
    setEditingDemandGenAd(null);
  }, []);

  // Don't render if no full structure
  if (!hasFullStructure) {
    return null;
  }

  // Meta short-circuit: render the Meta-specific accordion (ad sets →
  // ads + targeting). All write affordances are disabled per A3.5; A4
  // wires the real handlers.
  if (isMetaCampaign(campaign)) {
    return (
      <div className="bg-white rounded-lg shadow border">
        <div className="px-6 pt-4 pb-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Manage Campaign</h2>
          <p className="text-sm text-gray-500 mt-1">
            Manage ad sets, targeting, and ads. Write access for Meta arrives in a follow-up release.
          </p>
        </div>
        <MetaManagementAccordion campaign={campaign} />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border">
          {/* Section Header */}
          <div className="px-6 pt-4 pb-3 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Manage Campaign</h2>
            <p className="text-sm text-gray-500 mt-1">
              Manage campaign assets, {entityType === 'asset_group' ? 'asset groups' : 'ad groups'}, keywords, and ads.
            </p>
          </div>

          {/* Campaign Level Section */}
          {showCampaignLevel && (
            <CampaignLevelCard
              campaign={campaign}
              isExpanded={campaignLevelExpanded}
              onToggle={handleCampaignLevelToggle}
              selectedLandingPages={selectedLandingPages}
            />
          )}

          {/* Entity Cards Section */}
          {hasAnyEntities ? (
            <EntityCardList
              entities={entities}
              entityType={entityType}
              expandedId={expandedEntityId}
              onExpandEntity={handleExpandEntity}
              activeTabByEntity={activeTabByEntity}
              onTabChange={handleTabChange}
              campaign={campaign}
              selectedLandingPages={selectedLandingPages}
              rdaCache={rdaCache}
              isLoadingRDAs={isLoadingRDAs}
              demandGenCache={demandGenCache}
              isLoadingDemandGenAds={isLoadingDemandGenAds}
              onEditRSA={handleEditRSA}
              onEditRDA={handleEditRDA}
              onEditDemandGenAd={handleEditDemandGenAd}
              onCreateDemandGenAd={onCreateDemandGenAd}
              editingRSA={editingRSA}
              editingRDA={editingRDA}
              editingDemandGenAd={editingDemandGenAd}
              memberUuid={memberUuid}
              getRDAInitialData={getRDAInitialData}
              getDemandGenAdInitialData={getDemandGenAdInitialData}
              onEditorSaved={handleEditorSaved}
              onEditorCancel={handleEditorCancel}
            />
          ) : (
            <div className="p-6 bg-yellow-50 border-t border-yellow-200">
              <p className="text-yellow-800">
                {entityType === 'asset_group'
                  ? 'No asset groups found. This Performance Max campaign needs asset groups to manage.'
                  : 'No ad groups found. Navigate to this campaign from the Account page to load the full campaign structure.'}
              </p>
            </div>
          )}
    </div>
  );
}

export default CampaignManagement;
